import { useRef, useState, useMemo, useEffect } from 'react'
import type { ReactNode } from 'react'
import {
  ScrollerWrapper,
  SectionList,
  SectionBlock,
  SectionLabel,
  SectionLabelText,
  SectionLabelBadge,
  IndexBar,
  IndexItem,
} from './style'

interface Section<T> {
  key: string
  label: string
  shortLabel: string
  items: T[]
}

interface FastScrollerProps<T> {
  items: T[]
  getDate: (item: T) => string | Date
  renderItem: (item: T, index: number) => ReactNode
  showIndex?: boolean
}

function getPeriodKey(date: Date): string {
  const year = date.getFullYear()
  const half = date.getMonth() < 6 ? 1 : 2
  return `${year}-H${half}`
}

function getPeriodLabel(key: string): string {
  const [year, half] = key.split('-H')
  return half === '1' ? `janeiro - junho` : `julho - dezembro ${year}`
}

function getPeriodShort(key: string): string {
  const [year, half] = key.split('-H')
  return `S${half} ${year}`
}

export function OrderFastScroller<T>({ items, getDate, renderItem, showIndex = true }: FastScrollerProps<T>) {
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const sections = useMemo<Section<T>[]>(() => {
    const map = new Map<string, Section<T>>()
    for (const item of items) {
      const raw = getDate(item)
      const date = raw instanceof Date ? raw : new Date(raw)
      const key = getPeriodKey(date)
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: getPeriodLabel(key),
          shortLabel: getPeriodShort(key),
          items: [],
        })
      }
      map.get(key)!.items.push(item)
    }
    return Array.from(map.values()).sort((a, b) => b.key.localeCompare(a.key))
  }, [items, getDate])

  // init active to first section
  useEffect(() => {
    if (sections.length) setActiveSection(sections[0].key)
  }, [sections])

  // scroll spy — find which section is at/above the viewport top
  useEffect(() => {
    if (!sections.length) return

    function onScroll() {
      if (isScrollingRef.current) return
      const offset = 80 // px from top to consider "in view"
      let best: string | null = null

      for (const section of sections) {
        const el = sectionRefs.current[section.key]
        if (!el) continue
        const top = el.getBoundingClientRect().top
        if (top <= offset) {
          best = section.key
        }
      }

      if (best) setActiveSection(best)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // run once on mount to set initial state
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [sections])

  function scrollToSection(key: string) {
    const el = sectionRefs.current[key]
    if (el) {
      isScrollingRef.current = true
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
      const top = el.getBoundingClientRect().top + window.scrollY - 8
      window.scrollTo({ top, behavior: 'smooth' })
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false
      }, 800)
    }
    setActiveSection(key)
  }

  let globalIndex = 0

  return (
    <ScrollerWrapper>
      <SectionList>
        {sections.map((section) => (
          <SectionBlock
            key={section.key}
            ref={(el) => { sectionRefs.current[section.key] = el }}
          >
            <SectionLabel>
              <SectionLabelText>{section.label}</SectionLabelText>
              <SectionLabelBadge>{section.shortLabel}</SectionLabelBadge>
            </SectionLabel>
            {section.items.map((item) => renderItem(item, globalIndex++))}
          </SectionBlock>
        ))}
      </SectionList>

      {showIndex && sections.length > 1 && (
        <IndexBar>
          {sections.map((section) => (
            <IndexItem
              key={section.key}
              $active={activeSection === section.key}
              onClick={() => scrollToSection(section.key)}
              title={section.label}
            >
              {section.shortLabel}
            </IndexItem>
          ))}
        </IndexBar>
      )}
    </ScrollerWrapper>
  )
}
